import { Ref, RefObject, useEffect, useRef, useState } from "react";
import cx from "classnames";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";

import styles from "../../styles/HorizontalOverflow.module.less";

type HorizontalOverflowContainedProps = {
  children: React.ReactNode;
  ref?: Ref<HTMLDivElement>;
  scrollRef: RefObject<HTMLDivElement | null>;
  classNames?: {
    container?: string;
    scroller?: string;
    button?: string;
    next?: string;
    back?: string;
  };
  direction?: "left" | "right" | "both";
};

/**
 * HorizontalOverflow component, but expects a scrollRef to be passed in. This is useful for if the
 * parent component needs to monitor scrolling for some other reason, like to autoscroll to certain
 * elements
 */
const HorizontalOverflowContained = (props: HorizontalOverflowContainedProps) => {
  const { children, classNames, ref, scrollRef, direction = "both" } = props;
  const [isBackArrowEnabled, setIsBackArrowEnabled] = useState(false);
  const [isNextArrowEnabled, setIsNextArrowEnabled] = useState(false);
  const [hasTouchScrolled, setHasTouchScrolled] = useState(false);

  const toggleArrows = () => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    setIsBackArrowEnabled(scrollElement.scrollLeft > 0);
    setIsNextArrowEnabled(
      scrollElement.scrollWidth - scrollElement.clientWidth - scrollElement.scrollLeft > 0,
    );
  };

  const scrollBack = () => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    scrollElement.scrollLeft -= scrollElement.clientWidth;
  };

  const scrollNext = () => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    scrollElement.scrollLeft += scrollElement.clientWidth;
  };

  // Retoggle the positions on every render since the scroll size could change
  useEffect(() => {
    toggleArrows();
  });

  return (
    <div
      ref={ref}
      className={cx(styles.container, classNames?.container, hasTouchScrolled && styles.touched)}
    >
      <div
        ref={scrollRef}
        className={cx(styles.scroller, styles[direction], classNames?.scroller)}
        onScroll={() => toggleArrows()}
        onTouchMove={() => setHasTouchScrolled(true)}
      >
        {children}
      </div>
      <button
        type="button"
        onClick={scrollBack}
        disabled={!isBackArrowEnabled}
        aria-label="Scroll left"
        className={cx(styles.button, styles.back, classNames?.button, classNames?.back)}
      >
        <Icon className={styles.arrow} icon={faArrowLeft} />
      </button>
      <button
        type="button"
        onClick={scrollNext}
        disabled={!isNextArrowEnabled}
        aria-label="Scroll right"
        className={cx(styles.button, styles.next, classNames?.button, classNames?.next)}
      >
        <Icon className={styles.arrow} icon={faArrowRight} />
      </button>
    </div>
  );
};

type HorizontalOverflowProps = Omit<HorizontalOverflowContainedProps, "scrollRef"> & {
  ref?: Ref<HTMLDivElement>;
};

/**
 * Default mananaged HorizontalOverflow component. Allows content to break out of the container
 * and scroll to the edges (with some helpful arrows to help on desktop)
 */
const HorizontalOverflow = ({ ref, ...props }: HorizontalOverflowProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  return <HorizontalOverflowContained ref={ref} scrollRef={scrollRef} {...props} />;
};

export default Object.assign(HorizontalOverflow, { Contained: HorizontalOverflowContained });
